import {
  checkPersistentTimeout,
  clearPersistentTimeout,
  setPersistentTimeout
} from '../persistent-timers';
import { loggerFactory } from '../logger';

const activityMonitorLogger = loggerFactory('ActivityMonitor');

// @ts-ignore
const EventEmitter = require('events');

// There are two times we check for activity.
// The first one is at 90%.  This should give us a good
// buffer to grab another session.
export const CHECK_ACTIVITY_FACTOR = 0.95;
// The second timeout is at the limit of when we can call
// for a new session and expect to reasonably succeed.
export const LAST_CALL_FACTOR = 0.97;

const formatLogTime = (date: Date) => date.toISOString().replace('T', ' ').replace('Z', '');

export class ActivityMonitor extends EventEmitter {
  static readonly events = {
    /**
     *  A logout event from the application.
     */
    LOGOUT: 'LOGOUT',
    /**
     * A timeout from the application due to user inactivity.
     */
    TIMEOUT: 'TIMEOUT',
    /**
     * An event signalling that the authentication should be renewed.
     */
    SHOULD_REFRESH: 'SHOULD_REFRESH',
    /**
     * Start the process of renewing authentication.
     */
    BEGIN_REFRESH: 'BEGIN_REFRESH',
    /**
     * An event signalling that the authentication was renewed.
     */
    DID_REFRESH: 'DID_REFRESH',
    /**
     * Transfer a portion of the state from one Browsing Context to another.
     */
    STATE_UPDATE: 'STATE_UPDATE',
    /**
     * An event signalling that the activity monitoring period has been renewed.
     */
    RESET: 'RESET',
    /**
     * An event that activity has not been registered and that we need the user's
     * input that they want to continue their session.
     */
    ACTIVITY_PROMPT: 'ACTIVITY_PROMPT'
  };

  private _ifActiveTimeout: number;
  private _lastCallTimeout: number;
  private _isActive: boolean;
  private _contextId: number;

  constructor() {
    super();
    // check for the existence of the checkIfActive timer
    const checkIfActiveTimer = checkPersistentTimeout('checkIfActive');
    
    activityMonitorLogger.debug(`Existing timer for checking activity: ${checkIfActiveTimer}`);
    if (checkIfActiveTimer) {
      const lastCallTimer = checkPersistentTimeout('lastCall');
      this._setTimers(checkIfActiveTimer, lastCallTimer);
    }
  }

  /**
   * Checks if an authenticated browsing context is active.
   */
  checkIfActive() {
    activityMonitorLogger.debug(`ActivityMonitor checkIfActive ${this._isActive}`);
    const active = this._isActive;

    if (active) {
      this.beginRefresh();
    } else {
      // if there hasn't been activity, prompt the user.
      // if they are there at their keyboard, they can always
      // click something to let us know.
      activityMonitorLogger.log(
        `ActivityMonitor.events.ACTIVITY_PROMPT`
      );
      this.emit(ActivityMonitor.events.ACTIVITY_PROMPT);
    }
  }

  /**
   * Emit an event for consumers to take a new update of the application context.
   * @param appContext
   */
  shouldRefresh(appContext) {
    activityMonitorLogger.debug(`DID_REFRESH`);
    this.emit(ActivityMonitor.events.DID_REFRESH, appContext);
  }

  /**
   * Log the user out of the application.  Non-reversible!
   */
  logout() {
    this.emit(ActivityMonitor.events.LOGOUT);
  }

  /**
   * Emit an event that application should refresh the token.
   */
  beginRefresh() {
    activityMonitorLogger.debug(`BEGIN_REFRESH`);
    this.emit(ActivityMonitor.events.BEGIN_REFRESH);
    this.reset(`beginRefresh`);
  }

  /**
   * Transfer a portion of the state to the new redux state tree.
   * @param stateUpdate
   */
  replaceState(stateUpdate) {
    this.emit(ActivityMonitor.events.STATE_UPDATE, stateUpdate);
  }

  /**
   *  No longer monitor for activity.
   */
  stop() {
    if (this._ifActiveTimeout) {
      if (window.clearTimeout) {
        activityMonitorLogger.debug(`checkIfActive: clearTimeout`);
        window.clearTimeout(this._ifActiveTimeout);
      }
      this._ifActiveTimeout = 0;
      clearPersistentTimeout('checkIfActive');
    }
    if (this._lastCallTimeout) {
      if (window.clearTimeout) {
        activityMonitorLogger.debug(`checkIfActive: _lastCallTimeout`);
        window.clearTimeout(this._lastCallTimeout);
      }
      this._lastCallTimeout = 0;
      clearPersistentTimeout('lastCall');
    }
  }
  /**
   * Reset the timers for monitoring the application activity.
   */
  reset(optResetReason?: string) {
    activityMonitorLogger.debug(`reset: ${optResetReason}`, optResetReason);
    this.stop();
    // reset activity watch.
    this._isActive = false;
    this.emit(ActivityMonitor.events.RESET);
  }

  /**
   * Emit one last call to see if the application context/token should be refreshed.
   */
  lastCall() {
    activityMonitorLogger.debug(`lastCall: ${this._isActive}`);
    const active = this._isActive;
    if (!active) {
      this.emit(ActivityMonitor.events.TIMEOUT);
    } else {
      this.emit(ActivityMonitor.events.BEGIN_REFRESH);
      this.reset(`lastCall: ${ActivityMonitor.events.BEGIN_REFRESH}`);
    }
  }

  /**
   * The time (in milliseconds) by which the application should monitor for user activity.
   */
  set expiryTimeout(expiryTime: number) {
    // start over again with activity.
    activityMonitorLogger.debug(`setting expiryTime: ${expiryTime}`);
    const runTimeout = expiryTime * CHECK_ACTIVITY_FACTOR;
    const runLastCall = expiryTime * LAST_CALL_FACTOR;

    this._setTimers(runTimeout, runLastCall);
  }

  private _setTimers(runTimeout: number, runLastCall: number) {
    this.reset(`setting new timers`);
    const checkIfActiveTarget = new Date(new Date().getTime() + runTimeout);
    const lastCallTarget = new Date(new Date().getTime() + runTimeout);
    activityMonitorLogger.debug(
      `checkIfActive: will run in ${runTimeout /
        1000}s (${formatLogTime(checkIfActiveTarget)})` 
    );
    activityMonitorLogger.debug(
      `lastCall: will run in ${runLastCall /
        1000}s (${formatLogTime(lastCallTarget)})`
    );
    const checkIfActive = this.checkIfActive.bind(this);
    const lastCall = this.lastCall.bind(this);
    this._ifActiveTimeout = setPersistentTimeout(
      checkIfActive,
      runTimeout,
      'checkIfActive'
    );
    this._lastCallTimeout = setPersistentTimeout(
      lastCall,
      runLastCall,
      'lastCall'
    );
  }

  set contextId(id: number) {
    this._contextId = id;
  }
  get contextId() {
    return this._contextId;
  }
  set isActive(isActive: boolean) {
    this._isActive = isActive;
  }
}
